import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Headset } from 'lucide-react';
import { create } from 'zustand';

import { Button } from '@/components/ui/button';
import { useSupportAdminApi } from '../../../common/hooks/use-support-api.hook';
import type { InboxCounts, StaffMember } from '../../../common/models/support.model';
import { onRealtime } from '../../../common/services/realtime.service';

interface StaffSupportState {
  counts: InboxCounts | null;
  /** The support team, for assignee pickers and names. */
  staff: StaffMember[];
  setCounts: (c: InboxCounts) => void;
  setStaff: (s: StaffMember[]) => void;
}

/** Inbox counts and the team, shared by the launcher, sidebar, inbox and board. */
export const useStaffSupport = create<StaffSupportState>((set) => ({
  counts: null,
  staff: [],
  setCounts: (counts) => set({ counts }),
  setStaff: (staff) => set({ staff }),
}));

/** A teammate's name from their email; falls back to the part before the @. */
export function useStaffName() {
  const staff = useStaffSupport((s) => s.staff);
  return useCallback(
    (email?: string | null) => (email ? staff.find((m) => m.email === email)?.name || email.split('@')[0] : ''),
    [staff],
  );
}

/**
 * Keeps the staff inbox counts current: loads them once, then refreshes (at
 * most twice a second) whenever a conversation changes anywhere. Loads the
 * team once.
 */
export function StaffSupportSync() {
  const api = useSupportAdminApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        apiRef.current.getSummary().then((c) => c && useStaffSupport.getState().setCounts(c)).catch(() => undefined);
      }, 500);
    };
    apiRef.current.getSummary().then((c) => c && useStaffSupport.getState().setCounts(c)).catch(() => undefined);
    apiRef.current.listStaff().then((s) => s && useStaffSupport.getState().setStaff(s)).catch(() => undefined);
    const off = onRealtime('support:conversation', refresh);
    return () => {
      clearTimeout(timer);
      off();
    };
  }, []);

  return null;
}

/** Top-bar button for staff: to the inbox, with conversations awaiting a read. */
export function StaffSupportLauncher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const unread = useStaffSupport((s) => s.counts?.unread ?? 0);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative"
      onClick={() => navigate('/support')}
      aria-label={t('SUPPORT.STAFF.INBOX')}
      title={t('SUPPORT.STAFF.INBOX')}
    >
      <Headset />
      {unread > 0 && (
        <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-medium tabular-nums text-background">
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </Button>
  );
}
